/**
 * The watchdog's time base. Ticks come from a dedicated Worker because
 * main-thread timers and requestAnimationFrame are throttled or stopped in a
 * hidden tab; every interval is still measured with performance.now() on
 * arrival, so a delayed tick is seen as a long interval instead of being
 * trusted as a nominal one.
 */

export interface MonitorClock {
  /** Monotonic ms (performance.now()). */
  now(): number;
  /** Wall-clock ms (Date.now()). */
  wallNow(): number;
  /**
   * Begin ticking every `intervalMs`. `lagMs` is how long the tick waited
   * between being fired and being handled, which separates a blocked main
   * thread from a throttled timer.
   */
  start(intervalMs: number, onTick: (lagMs: number) => void): void;
  stop(): void;
  destroy(): void;
}

// Deadlines sit on a fixed grid from the start time so timer jitter does not
// accumulate; after a long pause the grid is rejoined rather than replayed.
// The message carries the worker's wall-clock fire time.
const WORKER_SOURCE = `
let timer = 0, epoch = 0, interval = 0, slot = 0;
function arm() {
  const now = performance.now();
  slot = Math.max(slot + 1, Math.ceil((now - epoch) / interval));
  timer = setTimeout(fire, epoch + slot * interval - now);
}
function fire() { postMessage(performance.timeOrigin + performance.now()); arm(); }
onmessage = (e) => {
  clearTimeout(timer);
  if (e.data.type !== 'start') return;
  epoch = performance.now();
  interval = e.data.interval;
  slot = 0;
  arm();
};
`;

export function createWorkerClock(): MonitorClock {
  let worker: Worker | null = null;
  let url: string | null = null;
  let handler: ((lagMs: number) => void) | null = null;

  return {
    now: () => performance.now(),
    wallNow: () => Date.now(),
    start(intervalMs, onTick) {
      handler = onTick;
      if (!worker) {
        url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
        worker = new Worker(url, { name: 'studio-health-clock' });
        worker.onmessage = (event: MessageEvent<number>) => {
          handler?.(Math.max(0, performance.timeOrigin + performance.now() - event.data));
        };
      }
      worker.postMessage({ type: 'start', interval: intervalMs });
    },
    stop() {
      handler = null;
      worker?.postMessage({ type: 'stop' });
    },
    destroy() {
      handler = null;
      if (worker) {
        worker.onmessage = null;
        worker.terminate();
        worker = null;
      }
      if (url) {
        URL.revokeObjectURL(url);
        url = null;
      }
    },
  };
}
